// src/storage/scannerStorage.ts — Schoolwork Scanner & Remediation Persistence (T-6.1.4, FR-SCAN-003)
// Stores schoolwork scans, diagnosed KS2 misconception codes, generated remediation quests,
// and linked performance telemetry with scanId foreign keys.

export interface DiagnosedMisconception {
  id: string;
  ks2Code: string; // e.g., "4F2", "5G4", "4E1", "4M7", "4N4"
  ks2Domain: string; // e.g., "Fractions & Decimals", "Angles & Geometry", "SPAG", "Word Problems"
  title: string;
  description: string;
  errorPattern: string;
  socraticInsight: string;
}

export interface RemediationQuestion {
  id: string;
  ks2Code: string;
  topic: string;
  prompt: string;
  contextText?: string;
  options: [string, string, string, string];
  correctOptionIndex: number;
  explanation: string;
  socraticHint: string;
}

export interface RemediationResult {
  totalQuestions: number; // exactly 5 (FR-SCAN-002)
  correctCount: number;
  answers: Record<string, number | null>;
  completedAt: number;
  earnedEp: number;
}

export interface SchoolworkScanRecord {
  id: string;
  createdAt: number;
  dateFormatted: string;
  title: string;
  sourceType: 'image_upload' | 'camera_capture' | 'text_entry';
  fileName?: string;
  fileSizeBytes?: number;
  imageDataUrl?: string;
  rawTextContent?: string;
  diagnosedMisconceptions: DiagnosedMisconception[];
  remediationQuestions: RemediationQuestion[];
  remediationResult?: RemediationResult;
}

const STORAGE_KEY = 'clawey_schoolwork_scans_v1';

export class ScannerStorage {
  /** Retrieve all saved schoolwork scans (newest first) */
  static loadScans(): SchoolworkScanRecord[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  /** Retrieve a single scan by scanId */
  static getScan(id: string): SchoolworkScanRecord | null {
    const list = this.loadScans();
    return list.find((item) => item.id === id) ?? null;
  }

  /** Persist a new scan */
  static saveScan(scan: SchoolworkScanRecord): void {
    try {
      const list = this.loadScans();
      const existingIdx = list.findIndex((item) => item.id === scan.id);
      if (existingIdx >= 0) {
        list[existingIdx] = scan;
      } else {
        list.unshift(scan);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // Handle storage quota gracefully
    }
  }

  /** Update remediation quest attempt linked to originating scanId (FR-SCAN-003) */
  static updateRemediationResult(scanId: string, result: RemediationResult): boolean {
    try {
      const list = this.loadScans();
      const scan = list.find((item) => item.id === scanId);
      if (!scan) return false;

      scan.remediationResult = result;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      return true;
    } catch {
      return false;
    }
  }

  /** Delete a scan record */
  static deleteScan(id: string): void {
    try {
      const list = this.loadScans().filter((item) => item.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // Ignore
    }
  }

  /** Clear all scans (test helper) */
  static clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}
